
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Hook that handles redirects based on authentication state
 */
export const useAuthRedirects = (
  user: any,
  isAuthenticated: boolean,
  isLoading: boolean
) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    try {
      // Skip redirects while loading
      if (isLoading) return;
      
      console.log("[AuthRedirects] Current auth state:", { 
        isAuthenticated, 
        path: location.pathname,
        user: user ? 'present' : 'not present'
      });
      
      // Get the current path
      const currentPath = location.pathname;
      
      // Public routes that don't require authentication
      const publicRoutes = [
        '/auth/login',
        '/auth/register',
        '/auth/reset-password',
        '/login', // Legacy route
        '/',
        '/blog',
        '/compro-oro',
        '/faq',
        '/gildy',
        '/avvisi' // Add /avvisi to public routes
      ];
      
      // Check if the current path starts with any of the public routes
      const isPublicRoute = publicRoutes.some(route => 
        currentPath === route || 
        (route !== '/' && currentPath.startsWith(route + '/'))
      );
      
      // Auth-only routes
      const authRoutes = [
        '/profilo',
        '/admin'
        // '/avvisi' - Removed from auth-only routes
      ];
      
      // Check if the current path starts with any of the auth-only routes
      const isAuthRoute = authRoutes.some(route => 
        currentPath === route || 
        currentPath.startsWith(route + '/')
      );
      
      // Handle 2FA verification
      if (user && user.requires_2fa && currentPath !== '/auth/verify-2fa') {
        console.log("[AuthRedirects] User requires 2FA verification, redirecting");
        navigate('/auth/verify-2fa');
        return;
      }
      
      // Redirect authenticated users away from auth pages
      if (isAuthenticated && (
        currentPath === '/auth/login' || 
        currentPath === '/auth/register' || 
        currentPath === '/login'
      )) {
        console.log("[AuthRedirects] User is authenticated, redirecting from auth page");
        navigate('/');
        return;
      }
      
      // Redirect unauthenticated users away from auth-only pages
      if (!isAuthenticated && isAuthRoute) {
        console.log("[AuthRedirects] User is not authenticated, redirecting to login");
        // Save the current location to redirect back after login
        navigate('/auth/login', { state: { from: location } });
        return;
      }
    } catch (error) {
      // Log error but don't break the application
      console.error("[AuthRedirects] Error in redirect logic:", error);
    }
  }, [user, isAuthenticated, isLoading, location, navigate]);
};
